import { useState } from 'react';
import type { RouteDetails } from './api';
import { ListActions, PrintHeading, SortSelect } from './CustomerTools';
import {
  cityStateZip,
  describeSort,
  mapLink,
  matchesCustomer,
  nextSort,
  searchTerms,
  sortStops,
  type CustomerRow,
  type SortKey,
  type SortState,
} from './customers';
import { buildCustomerWorkbook, downloadWorkbook, exportFileName } from './exportCustomers';
import { SortableHeader } from './SortableHeader';

const plural = (count: number, noun: string) => `${count} ${noun}${count === 1 ? '' : 's'}`;

/**
 * The customer table on one route's page: searchable, sortable by column,
 * with a map link per customer and export/print of the rows listed.
 */
export function RouteCustomers({
  route,
  accountName,
}: {
  route: RouteDetails;
  accountName: string;
}) {
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<SortState | null>(null);
  const query = search.trim();
  const terms = searchTerms(query);
  const visible = sortStops(
    terms.length ? route.stops.filter((stop) => matchesCustomer(stop, terms)) : route.stops,
    sort,
  );
  const title = `Route ${route.routeNumber}`;
  const toggleSort = (key: SortKey) => setSort((current) => nextSort(current, key));

  function exportExcel() {
    const rows: CustomerRow[] = visible.map((stop) => ({
      distributorId: route.distributorId,
      distributorName: route.distributorName,
      routeNumber: route.routeNumber,
      stop,
    }));
    const workbook = buildCustomerWorkbook({
      rows,
      scope: `${title} · ${route.distributorName}${query ? ` · customers matching “${query}”` : ''}`,
      account: accountName,
      search: query,
      sort: describeSort(sort),
    });
    downloadWorkbook(
      workbook,
      exportFileName(`route ${route.routeNumber} ${route.distributorName}`),
    );
  }

  return (
    <section className="customer-section" aria-labelledby="customer-list-title">
      <PrintHeading
        title={title}
        subtitle={route.distributorName}
        account={accountName}
        search={query}
        sort={sort}
        count={visible.length}
      />
      <div className="customer-list-heading">
        <div>
          <h3 id="customer-list-title">Customers on this route</h3>
          <p>
            {visible.length} of {plural(route.stops.length, 'customer')} shown
          </p>
        </div>
        <div className="customer-list-controls">
          <div className="toolbar-field customer-search">
            <label htmlFor="customer-search">Find a customer</label>
            <input
              id="customer-search"
              type="search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Name, street, city, ZIP, or phone"
            />
          </div>
          <SortSelect id="customer-sort" sort={sort} onChange={setSort} />
          <ListActions
            count={visible.length}
            onExport={exportExcel}
            onPrint={() => window.print()}
          />
        </div>
      </div>
      <div className="table-wrap stops-table-wrap">
        <table className="stops-table">
          <caption className="visually-hidden">
            Customers on route {route.routeNumber} for {route.distributorName}
          </caption>
          <thead>
            <tr>
              <SortableHeader column="name" label="Customer" sort={sort} onSort={toggleSort} />
              <SortableHeader
                column="street"
                label="Street address"
                sort={sort}
                onSort={toggleSort}
              />
              <SortableHeader
                column="place"
                label="City, state, ZIP"
                sort={sort}
                onSort={toggleSort}
              />
              <SortableHeader column="contact" label="Contact" sort={sort} onSort={toggleSort} />
              <SortableHeader column="phone" label="Phone" sort={sort} onSort={toggleSort} />
              <th scope="col" className="map-col">
                Map
              </th>
            </tr>
          </thead>
          <tbody>
            {visible.map((stop) => {
              const link = mapLink(stop);
              return (
                <tr key={stop.id}>
                  <th scope="row" data-label="Customer">
                    {stop.name}
                  </th>
                  <td data-label="Street address">{stop.streetAddress || '—'}</td>
                  <td data-label="City, state, ZIP">{cityStateZip(stop) || '—'}</td>
                  <td data-label="Contact">{stop.contactName || '—'}</td>
                  <td data-label="Phone">
                    {[stop.primaryPhone, stop.secondaryPhone].filter(Boolean).join(' · ') || '—'}
                  </td>
                  <td data-label="Map" className="map-col">
                    {link ? (
                      <a
                        className="map-link"
                        href={link}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={`Open a map for ${stop.name}`}
                      >
                        Map
                      </a>
                    ) : (
                      '—'
                    )}
                  </td>
                </tr>
              );
            })}
            {visible.length === 0 && (
              <tr>
                <td colSpan={6} className="no-route-results">
                  No customers on this route match “{query}”. Try part of a name, a street, a ZIP
                  code, or a phone number.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}
